import React from "react";
import { motion, useReducedMotion } from "framer-motion";

import { color } from "@/lib/theme";

/* ---------------- shared tokens ---------------- */
// Node tones, by the role the node plays in the diagram — the map exists only
// so a long data table stays readable. Keys are tone names, never hues.
const C = {
  brand: "brand",
  deep: "brand-deep",
  gold: "gold",
  valid: "valid",
  danger: "danger",
};
const ease = [0.16, 1, 0.3, 1];
const vp = { once: true, amount: 0.4 };

function Dot({ tone = C.brand, size = 8, glow = true }) {
  return (
    <span
      className="inline-block shrink-0 rounded-full"
      style={{ width: size, height: size, background: color(tone), boxShadow: glow ? `0 0 6px ${color(tone, 0.35)}` : "none" }}
    />
  );
}

function TechChip({ children, tone = C.brand, active = false }) {
  return (
    <span
      className="inline-flex items-center gap-2 rounded-full border px-3 py-1 font-mono text-[10px] uppercase tracking-[0.14em] transition-colors duration-300"
      style={{
        borderColor: active ? color(tone, 0.4) : color("line"),
        background: active ? color(tone, 0.08) : color("panel-raised"),
        color: color(active ? tone : "content-faint"),
      }}
    >
      {children}
    </span>
  );
}

/* ============================================================= */
/*  HERO — The Meira Trust Layer (radial hub)                    */
/* ============================================================= */

const RING = [
  ["AI Systems", C.brand],
  ["Models", C.brand],
  ["Agents", C.brand],
  ["Data", C.brand],
  ["Security Tests", C.deep],
  ["Risks", C.deep],
  ["Controls", C.deep],
  ["Policies", C.brand],
  ["Evidence", C.gold],
  ["Frameworks", C.brand],
].map(([label, tone], i, arr) => {
  const a = (-90 + i * (360 / arr.length)) * (Math.PI / 180);
  return { label, tone, x: 50 + 41 * Math.cos(a), y: 46 + 39 * Math.sin(a) };
});
const CENTER = { x: 50, y: 46 };
const RESOLVE = [
  ["Tested", C.deep],
  ["Governed", C.brand],
  ["Evidence", C.gold],
  ["Verified", C.valid],
];

export function TrustLayerHero() {
  const reduce = useReducedMotion();
  return (
    <div className="w-full" data-testid="trust-layer-hero-visual">
      {/* desktop radial */}
      <div className="relative mx-auto hidden aspect-square w-full max-w-[520px] lg:block">
        <svg className="absolute inset-0 h-full w-full" viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden>
          {RING.map((n, i) => (
            <motion.line
              key={n.label}
              x1={CENTER.x}
              y1={CENTER.y}
              x2={n.x}
              y2={n.y}
              stroke={color(n.tone === C.gold ? "gold" : "brand", n.tone === C.gold ? 0.5 : 0.3)}
              strokeWidth={n.tone === C.gold ? 0.8 : 0.6}
              vectorEffect="non-scaling-stroke"
              initial={reduce ? {} : { pathLength: 0, opacity: 0 }}
              whileInView={reduce ? {} : { pathLength: 1, opacity: 1 }}
              viewport={vp}
              transition={{ duration: 1.1, delay: 0.2 + i * 0.05, ease }}
            />
          ))}
        </svg>

        {/* ring nodes */}
        {RING.map((n, i) => (
          // The centring translate lives on a static wrapper: framer-motion writes an
          // inline `transform`, which would override a Tailwind -translate-*-1/2 here.
          <div
            key={n.label}
            className="absolute -translate-x-1/2 -translate-y-1/2"
            style={{ left: `${n.x}%`, top: `${n.y}%` }}
          >
            <motion.span
              className="flex flex-col items-center gap-1.5"
              initial={reduce ? {} : { opacity: 0, scale: 0.5 }}
              whileInView={reduce ? {} : { opacity: 1, scale: 1 }}
              viewport={vp}
              transition={{ duration: 0.6, delay: 0.3 + i * 0.05, ease }}
            >
              <Dot tone={n.tone} size={n.tone === C.gold ? 10 : 7} />
              <span className="whitespace-nowrap font-mono text-[9px] uppercase tracking-[0.12em] text-content-muted">{n.label}</span>
            </motion.span>
          </div>
        ))}

        {/* center */}
        <div
          className="absolute -translate-x-1/2 -translate-y-1/2"
          style={{ left: `${CENTER.x}%`, top: `${CENTER.y}%` }}
        >
          <motion.div
            className="relative flex h-[104px] w-[104px] items-center justify-center rounded-full border border-gold/40 bg-panel-raised/90 text-center backdrop-blur-sm"
            initial={reduce ? {} : { opacity: 0, scale: 0.6 }}
            whileInView={reduce ? {} : { opacity: 1, scale: 1 }}
            viewport={vp}
            transition={{ duration: 0.7, delay: 0.15, ease }}
          >
            {!reduce && <span className="absolute inset-0 animate-pulse-ring rounded-full bg-gold/40" />}
            <span className="relative z-10 px-2 font-mono text-[10px] uppercase leading-tight tracking-[0.16em] text-gold">
              Meira<br />Trust Layer
            </span>
          </motion.div>
        </div>
      </div>

      {/* resolve strip */}
      <motion.div
        className="mx-auto mt-4 flex max-w-[520px] flex-wrap items-center justify-center gap-2 lg:mt-6"
        initial={reduce ? {} : { opacity: 0, y: 12 }}
        whileInView={reduce ? {} : { opacity: 1, y: 0 }}
        viewport={vp}
        transition={{ duration: 0.6, delay: 0.6, ease }}
      >
        {RESOLVE.map(([label, tone], i) => (
          <React.Fragment key={label}>
            <TechChip tone={tone} active>{label}</TechChip>
            {i < RESOLVE.length - 1 && <span className="text-content-faint">→</span>}
          </React.Fragment>
        ))}
      </motion.div>

      {/* mobile simplified vertical chain */}
      <ol className="mx-auto flex max-w-xs flex-col gap-0 lg:hidden" aria-label="Meira trust layer chain">
        {["AI Systems", "Security Tests", "Risks & Controls", "Evidence", "Assurance", "Verified"].map((s, i, arr) => (
          <li key={s} className="relative pl-7">
            <span className="absolute left-[6px] top-1.5 h-2.5 w-2.5 rounded-full" style={{ background: color(i === arr.length - 1 ? C.valid : i >= 3 ? C.gold : C.brand) }} />
            {i < arr.length - 1 && <span className="absolute left-[10px] top-4 h-full w-px bg-line" />}
            <div className="pb-5">
              <div className="text-sm text-content">{s}</div>
            </div>
          </li>
        ))}
      </ol>
    </div>
  );
}

/* ============================================================= */
/*  01 — The question is changing (converge to PROVE IT.)         */
/* ============================================================= */

const QUESTIONS = ["Secure?", "Governed?", "Tested?", "Accountable?", "Compliant?", "Explainable?"];

export function QuestionConverge() {
  return (
    <div className="relative overflow-hidden rounded-lg border border-line bg-panel-raised/50 p-5 sm:p-7" data-testid="viz-question">
      <div className="flex flex-wrap gap-2">
        {QUESTIONS.map((q, i) => (
          <motion.span
            key={q}
            initial={{ opacity: 0, y: 8 }}
            whileInView={{ opacity: 0.55, y: 0 }}
            viewport={vp}
            transition={{ duration: 0.5, delay: i * 0.08, ease }}
            className="rounded-full border border-line bg-panel-raised/60 px-3 py-1 font-mono text-[11px] uppercase tracking-[0.12em] text-content-faint"
          >
            {q}
          </motion.span>
        ))}
      </div>
      <div className="my-6 flex items-center gap-3">
        <span className="h-px flex-1 bg-gradient-to-r from-transparent to-gold/40" />
        <motion.span
          initial={{ opacity: 0, scale: 0.9 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={vp}
          transition={{ duration: 0.6, delay: 0.5, ease }}
          className="font-display text-2xl font-medium text-gold"
        >
          Prove it.
        </motion.span>
        <span className="h-px flex-1 bg-gradient-to-l from-transparent to-gold/40" />
      </div>
      <div className="flex items-center justify-center gap-3 font-mono text-[10px] uppercase tracking-[0.16em]">
        <span className="text-content-muted">AI System</span>
        <span className="text-content-faint">→</span>
        <span className="text-gold">Evidence</span>
        <span className="text-content-faint">→</span>
        <span className="text-valid">Verification</span>
      </div>
    </div>
  );
}

/* ============================================================= */
/*  Reusable motif — Claim → Evidence → Verification → Trust      */
/* ============================================================= */

const MOTIF = [
  ["Claim", "content-faint", "neutral"],
  ["Evidence", C.brand, "evidence"],
  ["Verification", C.gold, "gold"],
  ["Trust", C.valid, "verified"],
];

export function ClaimEvidenceTrust() {
  return (
    <div className="rounded-lg border border-line bg-panel-raised/50 p-5 sm:p-7" data-testid="viz-motif">
      <div className="flex flex-col gap-0 sm:flex-row sm:items-center sm:justify-between">
        {MOTIF.map(([label, tone], i) => (
          <React.Fragment key={label}>
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={vp}
              transition={{ duration: 0.5, delay: i * 0.12, ease }}
              className="flex items-center gap-3 py-2 sm:flex-col sm:py-0 sm:text-center"
            >
              <span className="flex h-11 w-11 items-center justify-center rounded-full border" style={{ borderColor: color(tone, 0.33), background: color(tone, 0.07) }}>
                <Dot tone={tone} size={10} />
              </span>
              <span className="font-mono text-[10px] uppercase tracking-[0.16em] sm:mt-3" style={{ color: color(tone) }}>{label}</span>
            </motion.div>
            {i < MOTIF.length - 1 && (
              <span className="ml-[21px] h-6 w-px bg-line sm:ml-0 sm:h-px sm:flex-1 sm:bg-gradient-to-r sm:from-line-bright sm:to-line-soft" />
            )}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
}

/* ============================================================= */
/*  03 — Security + Governance converge                           */
/* ============================================================= */

const SEC = ["Red Teaming", "Model Testing", "Agent Testing", "Findings", "Risk"];
const GOV = ["Inventory", "Ownership", "Policies", "Controls", "Frameworks"];

function StreamColumn({ title, items, tone, align = "left" }) {
  return (
    <div>
      <div className="mb-4 flex items-center gap-2" style={{ justifyContent: align === "right" ? "flex-end" : "flex-start" }}>
        <Dot tone={tone} size={7} />
        <span className="font-mono text-[10px] uppercase tracking-[0.18em]" style={{ color: color(tone) }}>{title}</span>
      </div>
      <div className={`flex flex-col gap-2 ${align === "right" ? "items-end" : "items-start"}`}>
        {items.map((it, i) => (
          <motion.span
            key={it}
            initial={{ opacity: 0, x: align === "right" ? 12 : -12 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={vp}
            transition={{ duration: 0.45, delay: i * 0.06, ease }}
            className="rounded-md border border-line bg-panel-raised px-3 py-1.5 text-[12px] text-content"
          >
            {it}
          </motion.span>
        ))}
      </div>
    </div>
  );
}

export function SecurityGovernanceConverge() {
  return (
    <div className="relative overflow-hidden rounded-lg border border-line bg-panel-raised/40 p-5 sm:p-7" data-testid="viz-converge">
      <div className="grid grid-cols-2 gap-3 sm:gap-6">
        <StreamColumn title="AI Security" items={SEC} tone={C.danger} align="left" />
        <StreamColumn title="AI Governance" items={GOV} tone={C.brand} align="right" />
      </div>
      <div className="relative mt-6 flex justify-center">
        <span className="h-6 w-px bg-gradient-to-b from-line-bright to-gold/40" />
      </div>
      <div className="flex flex-col items-center gap-3">
        <span className="rounded-md border border-gold/30 bg-gold/[0.06] px-4 py-2 font-mono text-[11px] uppercase tracking-[0.16em] text-gold">
          Evidence Layer
        </span>
        <span className="h-4 w-px bg-line" />
        <div className="flex items-center gap-3 font-mono text-[10px] uppercase tracking-[0.16em]">
          <span className="text-content-muted">Assurance</span>
          <span className="text-content-faint">→</span>
          <span className="text-valid">Verification</span>
        </div>
      </div>
    </div>
  );
}

/* ============================================================= */
/*  04 — Continuous assurance lifecycle (loop)                    */
/* ============================================================= */

const LIFE = ["Assess", "Test", "Govern", "Prove", "Monitor", "Change detected"];

export function ContinuousLifecycle() {
  const reduce = useReducedMotion();
  const nodes = LIFE.map((label, i, arr) => {
    const a = (-90 + i * (360 / arr.length)) * (Math.PI / 180);
    return { label, x: 50 + 34 * Math.cos(a), y: 50 + 36 * Math.sin(a) };
  });
  return (
    <div className="relative mx-auto aspect-square w-full max-w-[380px] rounded-lg border border-line bg-panel-raised/40" data-testid="viz-lifecycle">
      <svg className="absolute inset-0 h-full w-full" viewBox="0 0 100 100" aria-hidden>
        <motion.circle
          cx="50" cy="50" r="35" fill="none" stroke={color("brand", 0.25)} strokeWidth="0.5"
          strokeDasharray="3 3"
          initial={reduce ? {} : { rotate: 0 }}
          animate={reduce ? {} : { rotate: 360 }}
          transition={{ duration: 60, repeat: Infinity, ease: "linear" }}
          style={{ transformOrigin: "50% 50%" }}
        />
      </svg>
      {nodes.map((n, i) => (
        <div key={n.label} className="absolute -translate-x-1/2 -translate-y-1/2 text-center" style={{ left: `${n.x}%`, top: `${n.y}%` }}>
          <span className="flex flex-col items-center gap-1">
            <Dot tone={i === 5 ? C.gold : C.brand} size={7} />
            <span className="whitespace-nowrap font-mono text-[9px] uppercase tracking-[0.1em] text-content-muted">{n.label}</span>
          </span>
        </div>
      ))}
      <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-center">
        <div className="font-mono text-[9px] uppercase tracking-[0.16em] text-content-faint">Continuous</div>
        <div className="font-display text-lg font-medium text-content">Assurance</div>
        <div className="mt-1 font-mono text-[8px] uppercase tracking-[0.14em] text-gold">Re-provable</div>
      </div>
    </div>
  );
}

/* ============================================================= */
/*  SIGNATURE — AI Trust Architecture (Meira runs across)         */
/* ============================================================= */

const LAYERS = [
  ["AI Systems", ["Models", "Agents", "Applications", "APIs"], C.brand],
  ["Security", ["Testing", "Red Teaming", "Findings", "Risk"], C.deep],
  ["Governance", ["Inventory", "Ownership", "Policies", "Controls", "Frameworks"], C.brand],
  ["Evidence", ["Tests", "Decisions", "Approvals", "Artifacts", "Provenance"], C.gold],
  ["Assurance", ["Trust Passport", "Readiness", "Verification"], C.valid],
];

export function TrustArchitecture() {
  return (
    <div className="grid grid-cols-[auto_1fr] gap-4 sm:gap-5" data-testid="viz-architecture">
      {/* cross-cutting Meira band */}
      <div className="relative flex w-12 items-center justify-center overflow-hidden rounded-lg border border-gold/30 bg-gradient-to-b from-gold/[0.10] to-brand/[0.06] sm:w-14">
        <span className="whitespace-nowrap font-mono text-[10px] uppercase tracking-[0.28em] text-gold sm:text-[11px]" style={{ writingMode: "vertical-rl", transform: "rotate(180deg)" }}>
          Meira Trust Layer
        </span>
      </div>

      {/* stacked layers */}
      <div className="flex flex-col gap-3">
        {LAYERS.map(([name, chips, tone], i) => (
          <motion.div
            key={name}
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={vp}
            transition={{ duration: 0.55, delay: i * 0.1, ease }}
            className="relative overflow-hidden rounded-lg border border-line bg-panel-raised/70 p-4 sm:p-5"
          >
            <span className="absolute left-0 top-0 h-full w-[3px]" style={{ background: color(tone) }} />
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
              <span className="flex items-center gap-2">
                <Dot tone={tone} size={8} />
                <span className="font-display text-base font-medium text-content sm:text-lg">{name}</span>
              </span>
              <span className="flex flex-wrap gap-1.5">
                {chips.map((c) => (
                  <span key={c} className="rounded-full border border-line bg-panel-raised px-2.5 py-0.5 font-mono text-[9px] uppercase tracking-wide text-content-muted">
                    {c}
                  </span>
                ))}
              </span>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
}

/* ============================================================= */
/*  Global trust network (abstract, no world map)                */
/* ============================================================= */

const CLUSTERS = [
  { nodes: [[14, 22], [24, 34], [10, 42], [30, 20]], label: "Environment A" },
  { nodes: [[50, 14], [58, 28], [44, 30], [66, 18]], label: "Environment B" },
  { nodes: [[82, 24], [90, 38], [74, 34], [88, 18]], label: "Environment C" },
];
const HUB = { x: 50, y: 82 };
const GLOBAL_FRAMEWORKS = ["ISO/IEC 42001", "EU AI Act", "NIST AI RMF", "OWASP LLM", "UAE requirements"];

export function GlobalTrustNetwork() {
  const reduce = useReducedMotion();
  const allNodes = CLUSTERS.flatMap((c) => c.nodes);
  return (
    <div data-testid="viz-global">
      <div className="relative aspect-[16/10] w-full overflow-hidden rounded-lg border border-line bg-panel-raised/40 tech-grid">
        <svg className="absolute inset-0 h-full w-full" viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden>
          {/* intra-cluster links */}
          {CLUSTERS.map((c, ci) =>
            c.nodes.slice(1).map(([x, y], i) => (
              <line key={`c${ci}-${i}`} x1={c.nodes[0][0]} y1={c.nodes[0][1]} x2={x} y2={y} stroke={color("brand", 0.2)} strokeWidth="0.3" vectorEffect="non-scaling-stroke" />
            ))
          )}
          {/* cluster → hub */}
          {CLUSTERS.map((c, ci) => (
            <motion.line
              key={`h${ci}`}
              x1={c.nodes[0][0]} y1={c.nodes[0][1]} x2={HUB.x} y2={HUB.y}
              stroke={color("gold", 0.35)} strokeWidth="0.45" vectorEffect="non-scaling-stroke"
              initial={reduce ? {} : { pathLength: 0, opacity: 0 }}
              whileInView={reduce ? {} : { pathLength: 1, opacity: 1 }}
              viewport={vp}
              transition={{ duration: 1.2, delay: 0.3 + ci * 0.15, ease }}
            />
          ))}
        </svg>

        {allNodes.map(([x, y], i) => (
          <span key={i} className="absolute -translate-x-1/2 -translate-y-1/2" style={{ left: `${x}%`, top: `${y}%` }}>
            <motion.span
              className="block rounded-full"
              style={{ width: 7, height: 7, background: color(C.brand), boxShadow: `0 0 6px ${color(C.brand, 0.53)}` }}
              initial={reduce ? {} : { opacity: 0, scale: 0.4 }}
              whileInView={reduce ? {} : { opacity: 1, scale: 1 }}
              viewport={vp}
              transition={{ duration: 0.5, delay: i * 0.04, ease }}
            />
          </span>
        ))}

        {/* hub */}
        <div className="absolute -translate-x-1/2 -translate-y-1/2 text-center" style={{ left: `${HUB.x}%`, top: `${HUB.y}%` }}>
          <span className="inline-block rounded-full border border-gold/40 bg-panel-raised/90 px-3 py-1.5 font-mono text-[8.5px] uppercase leading-tight tracking-[0.12em] text-gold backdrop-blur-sm sm:px-4 sm:py-2 sm:text-[10px] sm:tracking-[0.16em]">
            One connected evidence layer
          </span>
        </div>
      </div>
      <div className="mt-5 flex flex-wrap gap-2">
        {GLOBAL_FRAMEWORKS.map((f) => (
          <span key={f} className="rounded-full border border-line bg-panel-raised px-3 py-1 font-mono text-[10px] tracking-wide text-content-faint">
            {f}
          </span>
        ))}
      </div>
    </div>
  );
}

/* ============================================================= */
/*  Product architecture — DISCOVER → ... → VERIFY               */
/* ============================================================= */

const STAGES = [
  ["Discover", "AI Inventory", C.brand],
  ["Test", "AI Security · Red Teaming", C.deep],
  ["Govern", "Risk · Controls · Ownership", C.brand],
  ["Evidence", "Connected Evidence Layer", C.gold],
  ["Assure", "Trust Passport · Readiness", C.gold],
  ["Verify", "Verifiable Assurance", C.valid],
];

const CAPABILITIES = [
  ["Trust Graph", "Preview"],
  ["AI Governance Digital Twin", "Concept"],
  ["Regulation-to-Control Mapping", "Preview"],
  ["Continuous AI Security Testing", "Planned"],
  ["Evidence Automation", "Concept"],
  ["Verifiable Certificates", "Preview"],
  ["Trust Passport", "Preview"],
];

const STATUS_STYLE = {
  Available: "border-valid/30 bg-valid/10 text-valid",
  Preview: "border-brand/30 bg-brand/10 text-brand-soft",
  Concept: "border-line bg-panel-raised text-content-muted",
  Planned: "border-line bg-panel-raised text-content-muted",
};

export function ProductArchitecture() {
  return (
    <div data-testid="viz-product">
      <div className="flex flex-col lg:flex-row lg:items-stretch">
        {STAGES.map(([stage, sub, tone], i) => (
          <React.Fragment key={stage}>
            <motion.div
              initial={{ opacity: 0, y: 14 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={vp}
              transition={{ duration: 0.5, delay: i * 0.07, ease }}
              className="relative flex-1 overflow-hidden rounded-lg border border-line bg-panel-raised p-5"
            >
              <span className="absolute left-0 top-0 h-full w-[3px]" style={{ background: color(tone) }} />
              <div className="flex items-center gap-2">
                <Dot tone={tone} size={7} />
                <span className="font-mono text-[10px] uppercase tracking-[0.18em]" style={{ color: color(tone) }}>{stage}</span>
              </div>
              <p className="mt-2 text-[13px] leading-snug text-content-muted">{sub}</p>
            </motion.div>
            {i < STAGES.length - 1 && (
              <div className="flex items-center justify-center" aria-hidden>
                <span className="my-1 h-5 w-px bg-gradient-to-b from-brand/50 to-gold/50 lg:my-0 lg:h-px lg:w-6 lg:bg-gradient-to-r" />
              </div>
            )}
          </React.Fragment>
        ))}
      </div>

      <div className="mt-8">
        <div className="mb-4 font-mono text-[10px] uppercase tracking-[0.2em] text-content-faint">Supporting capabilities</div>
        <div className="flex flex-wrap gap-2.5">
          {CAPABILITIES.map(([cap, status]) => (
            <span key={cap} className="inline-flex items-center gap-2 rounded-lg border border-line bg-panel-raised/60 px-3 py-2">
              <span className="text-[12px] text-content">{cap}</span>
              <span className={`rounded-full border px-2 py-0.5 font-mono text-[8px] uppercase tracking-[0.12em] ${STATUS_STYLE[status]}`}>{status}</span>
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}
